import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { RootStateInterface } from "reducer";

import { LayerAlias } from "constant";
import { LayersManagerStateInterface } from "scenes/LayersManager/interfaces";

import IconButton from "components/buttons/IconButton/IconButton";

import CloseSmallIcon from "assets/icons/close-small.svg";

import HistogramRange from "components/HistogramRange/HistogramRange";
import features from "features";
import { useDispatch } from "react-redux";
import styles from "./map-layers.module.scss";

interface EditPolygonsRescalePortalInterface {
  alias: LayerAlias;
  onClose: () => void;
}

const EditPolygonsRescalePortal = ({
  alias,
  onClose
}: EditPolygonsRescalePortalInterface) => {
  const dispatch = useDispatch();
  const formRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 100, left: 100 });
  const [size] = useState<{ width: number; height: number }>({
    width: 400,
    height: 300
  });
  const [isDragging, setIsDragging] = useState(false);
  // const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(
    null
  );
  // const [resizeOffset, setResizeOffset] = useState<{
  //   x: number;
  //   y: number;
  // } | null>(null);

  const { layers } = useSelector<
    RootStateInterface,
    LayersManagerStateInterface
  >((state) => state.layersManager);

  const selectedLayer = useMemo(
    () => layers.find((item) => item.alias === alias),
    [alias, layers]
  );

  const getStatistics = useCallback(
    (filters) => {
      return filters.find((item) => item.formula === selectedLayer.filter.name)
        ?.statistics;
    },
    [selectedLayer.filter]
  );

  const onUpdateRange = useCallback(
    (providerId) => (percentiles) => {
      dispatch(
        features.layersManager.actions.setProviderFilterPercentiles({
          alias,
          providerId,
          percentiles
        })
      );
    },
    [alias, dispatch]
  );

  const updatePosition = useCallback((targetElement: HTMLElement | null) => {
    if (!targetElement) return;

    const elRect = targetElement.getBoundingClientRect();
    const formWidth = size.width;

    let top = elRect.bottom + window.scrollY + 5;
    let left = elRect.right + window.scrollX - formWidth + 16;

    if (left < 0) {
      left = 5;
    }

    setPosition({ top, left });
  }, []);

  useEffect(() => {
    const targetElement = document.getElementById(`${alias}-layer-block`);
    updatePosition(targetElement);
  }, [alias, updatePosition]);

  const handleMouseDownDrag = (event: React.MouseEvent) => {
    const rect = formRef.current?.getBoundingClientRect();
    if (rect) {
      setIsDragging(true);
      setDragOffset({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      });
    }
  };

  const handleMouseMoveDrag = useCallback(
    (event: MouseEvent) => {
      if (!isDragging || !dragOffset) return;

      setPosition({
        top: event.clientY - dragOffset.y,
        left: event.clientX - dragOffset.x
      });
    },
    [dragOffset, isDragging]
  );

  const handleMouseUpDrag = () => {
    setIsDragging(false);
    setDragOffset(null);
  };

  // const handleMouseDownResize = (event: React.MouseEvent) => {
  //   const rect = formRef.current?.getBoundingClientRect();
  //   if (rect) {
  //     setIsResizing(true);
  //     setResizeOffset({
  //       x: event.clientX - rect.right,
  //       y: event.clientY - rect.bottom
  //     });
  //   }
  // };

  // const handleMouseMoveResize = useCallback(
  //   (event: MouseEvent) => {
  //     if (!isResizing || !resizeOffset) return;

  //     setSize({
  //       width: Math.max(200, event.clientX - position.left + resizeOffset.x),
  //       height: Math.max(150, event.clientY - position.top + resizeOffset.y)
  //     });
  //   },
  //   [isResizing, position, resizeOffset]
  // );

  // const handleMouseUpResize = () => {
  //   setIsResizing(false);
  //   setResizeOffset(null);
  // };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMoveDrag);
      window.addEventListener("mouseup", handleMouseUpDrag);
    } else {
      window.removeEventListener("mousemove", handleMouseMoveDrag);
      window.removeEventListener("mouseup", handleMouseUpDrag);
    }

    // if (isResizing) {
    //   window.addEventListener("mousemove", handleMouseMoveResize);
    //   window.addEventListener("mouseup", handleMouseUpResize);
    // } else {
    //   window.removeEventListener("mousemove", handleMouseMoveResize);
    //   window.removeEventListener("mouseup", handleMouseUpResize);
    // }

    return () => {
      window.removeEventListener("mousemove", handleMouseMoveDrag);
      window.removeEventListener("mouseup", handleMouseUpDrag);
      // window.removeEventListener("mousemove", handleMouseMoveResize);
      // window.removeEventListener("mouseup", handleMouseUpResize);
    };
  }, [
    isDragging,
    dragOffset,
    // isResizing,
    // resizeOffset,
    handleMouseMoveDrag
    // handleMouseMoveResize
  ]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  if (!selectedLayer) return null;

  return createPortal(
    <div
      ref={formRef}
      style={{
        top: position.top,
        left: position.left,
        width: size.width,
        height: size.height,
        position: "absolute"
      }}
      className={styles["edit-polygons-rescale-portal-container"]}
    >
      <h5
        className={styles["title"]}
        onMouseDown={handleMouseDownDrag}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      >
        Histograms
      </h5>
      <IconButton
        icon={CloseSmallIcon}
        className={styles["close"]}
        alt="close"
        onClick={onClose}
      />
      {/* <div
        className={styles["resize-handle"]}
        onMouseDown={handleMouseDownResize}
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: "10px",
          height: "10px",
          background: "gray",
          cursor: "se-resize"
        }}
      /> */}
      <div className={styles["providers-list"]}>
        {selectedLayer.providers.map((item) => (
          <div key={item.id}>
            <div>{item.data.name}</div>
            {getStatistics(item.data.filters) && (
              <HistogramRange
                statistics={getStatistics(item.data.filters)}
                onUpdateRange={onUpdateRange(item.id)}
              />
            )}
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default EditPolygonsRescalePortal;
